"use client";

import React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/utils/fontsConfig";
import { Label, SubLabel } from "~/components/v3/shadcn/Label";

export const rootVariants = cva(
    "focus-visible:ring-ring peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-v3-M500 data-[state=unchecked]:bg-gray-700",
    {
        variants: {
            variant: {
                default: "",
            },
            size: {
                default: "h-[24px] w-[44px]",
                sm: "h-[18px] w-[35px]",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

export const thumbVariants = cva(
    "pointer-events-none block rounded-full bg-white ring-0 transition-transform",
    {
        variants: {
            variant: {
                default: "",
            },
            size: {
                default:
                    "h-5 w-5 shadow-lg data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0",
                sm: "h-3 w-3 shadow-lg data-[state=checked]:translate-x-[18px] data-[state=unchecked]:translate-x-[2px]",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

export interface SwitchProps
    extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
        VariantProps<typeof rootVariants> {
    asChild?: boolean;
}

const Switch = React.forwardRef<
    React.ElementRef<typeof SwitchPrimitives.Root>,
    SwitchProps
>(({ className, variant, size, asChild = false, ...props }, ref) => (
    <SwitchPrimitives.Root
        className={cn(rootVariants({ variant, size, className }))}
        {...props}
        ref={ref}
    >
        <SwitchPrimitives.Thumb
            className={cn(thumbVariants({ variant, size }))}
        />
    </SwitchPrimitives.Root>
));
Switch.displayName = SwitchPrimitives.Root.displayName;

interface SwitchFieldProps
    extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
    label: string;
    subLabel?: string;
}

const SwitchField = React.forwardRef<
    React.ElementRef<typeof SwitchPrimitives.Root>,
    SwitchProps & VariantProps<typeof rootVariants> & SwitchFieldProps
>(({ className, variant, size, asChild = false, ...props }, ref) => {
    return (
        <div className="flex items-center gap-2">
            <SwitchPrimitives.Root
                className={cn(rootVariants({ variant, size, className }))}
                {...props}
                ref={ref}
            >
                <SwitchPrimitives.Thumb
                    className={cn(thumbVariants({ variant, size }))}
                />
            </SwitchPrimitives.Root>
            <div className="flex flex-col gap-2 transition-colors">
                <Label
                    className={props.checked ? "text-white" : "text-v3-N-300"}
                >
                    {props.label}
                </Label>
                {props.subLabel && (
                    <SubLabel
                        className={
                            props.checked ? "text-white" : "text-v3-N-300"
                        }
                    >
                        {props.subLabel}
                    </SubLabel>
                )}
            </div>
        </div>
    );
});
SwitchField.displayName = SwitchPrimitives.Root.displayName;

export { Switch, SwitchField };
